import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Team } from '../types';
import { ApiError } from '../utils/error-handler';
import { teamService } from '../../services';
import { extractErrorMessage } from '../utils/error-handler';
import { useOrganizationTeams as useOrganizationTeamsQuery } from './queries';
import { queryKeys } from '../config/react-query.config';

/**
 * Hook for managing organization teams with React Query
 *
 * This replaces the old manual fetch implementation with React Query,
 * which provides automatic deduplication and caching.
 *
 * If 5 components call this hook with the same organizationId,
 * React Query makes ONE API call and shares the result.
 */
export function useOrganizationTeams(organizationId: string) {
  const queryClient = useQueryClient();

  // Use React Query for fetching (auto-deduplication, caching)
  const {
    data: teams = [],
    isLoading,
    error: queryError,
    refetch
  } = useOrganizationTeamsQuery(organizationId);

  // Convert query error to ApiError format for backward compatibility
  const error: ApiError | null = queryError ? extractErrorMessage(queryError) : null;

  // Wrap refetch for backward compatibility
  const fetchTeams = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Mutations for create/update/delete
  const createTeamMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      teamService.createTeam({ ...data, organizationId }),
    onSuccess: () => {
      // Invalidate queries to refetch latest data
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.teams(organizationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() });
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: ({ teamId, data }: { teamId: string; data: { name?: string; description?: string } }) =>
      teamService.updateTeam(teamId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.teams(organizationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() });
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: (teamId: string) => teamService.deleteTeam(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.teams(organizationId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() });
    },
  });

  // Wrapper functions for backward compatibility
  const createTeam = async (data: { name: string; description?: string }) => {
    try {
      await createTeamMutation.mutateAsync(data);
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      return { success: false, error };
    }
  };

  const updateTeam = async (teamId: string, data: { name?: string; description?: string }) => {
    try {
      await updateTeamMutation.mutateAsync({ teamId, data });
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      return { success: false, error };
    }
  };

  const deleteTeam = async (teamId: string) => {
    try {
      await deleteTeamMutation.mutateAsync(teamId);
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      return { success: false, error };
    }
  };
  
  return {
    teams,
    isLoading,
    error,
    refreshTeams: fetchTeams,
    createTeam,
    updateTeam,
    deleteTeam
  };
}

export default useOrganizationTeams;